import { useState, useEffect, useCallback, useRef, useLayoutEffect, useMemo } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, Search, ArrowRight, ArrowLeft, Calendar, User, Loader2, ChevronLeft, ChevronRight, List, Mail, ShieldCheck, X, PhoneCall } from 'lucide-react';
import { GuideQuiz } from './GuideQuiz';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const isRealImg = (u) => u && !/CTG-1\.png/i.test(u);

const CtcMark = ({ className }) => (
  <svg viewBox="0 0 64 64" className={className} fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <clipPath id="ctc-mark-clip-kh"><rect width="64" height="64" rx="15" /></clipPath>
    <g clipPath="url(#ctc-mark-clip-kh)">
      <rect width="32" height="64" fill="#1A3A5F" />
      <rect x="32" width="32" height="64" fill="#00A79D" />
    </g>
    <path d="M17 33.5 L27 43.5 L47 21" stroke="#FFFFFF" strokeWidth="6.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BrandPlaceholder = ({ markClass = 'h-14 w-auto' }) => (
  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#E9F7F5] via-[#F8F9FA] to-[#EAF6FB]">
    <div className="flex flex-col items-center gap-2">
      <CtcMark className={markClass} />
      <span className="ctc-wordmark text-sm leading-none">
        <span className="font-extrabold text-[#1A3A5F]">care</span>
        <span className="font-extrabold text-[#00A79D]">tech</span>
        <span className="font-extrabold text-[#1A3A5F]">compare</span>
      </span>
    </div>
  </div>
);

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return ''; }
};

const ArticleCard = ({ a }) => (
  <Link to={`/knowledge-hub/${a.slug}`} className="group bg-white border border-[#E5E7EB] rounded-xl overflow-hidden hover:border-[#00A79D]/50 hover:shadow-md transition-all flex flex-col" data-testid={`article-card-${a.slug}`}>
    <div className="aspect-[16/9] bg-[#F1F5F4] overflow-hidden">
      {isRealImg(a.image)
        ? <img src={a.image} alt={a.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300" />
        : <BrandPlaceholder />}
    </div>
    <div className="p-5 flex flex-col flex-1">
      <div className="flex flex-wrap gap-1.5 mb-2">
        {(a.categories || []).slice(0, 2).map((c) => (
          <span key={c} className="text-[10px] font-semibold uppercase tracking-wide text-[#00857D] bg-[#00A79D]/10 px-2 py-0.5 rounded">{c}</span>
        ))}
      </div>
      <h3 className="font-heading font-bold text-gray-900 leading-snug group-hover:text-[#00857D] transition-colors">{a.title}</h3>
      {a.excerpt && <p className="text-sm text-gray-600 mt-2 line-clamp-2 flex-1">{a.excerpt}</p>}
      <div className="flex items-center gap-3 mt-4 pt-3 border-t border-[#F1F1F4] text-xs text-gray-500">
        <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5" /> {a.author}</span>
        <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {fmtDate(a.published_at)}</span>
      </div>
    </div>
  </Link>
);

const CatButton = ({ active, onClick, label, count, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`flex items-center justify-between w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${active ? 'bg-[#00A79D]/10 text-[#00857D] font-semibold' : 'text-gray-600 hover:bg-gray-50'}`}>
    <span>{label}</span>
    {typeof count === 'number' && <span className="text-xs text-gray-400">{count}</span>}
  </button>
);

export const KnowledgeHub = () => {
  const [searchParams] = useSearchParams();
  const guides = searchParams.get('guides') === '1';
  const [meta, setMeta] = useState({ total: 0, categories: [] });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState(searchParams.get('cat') || 'All');
  const [loading, setLoading] = useState(true);
  const perPage = 12;
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  useEffect(() => { axios.get(`${API}/public/blog/meta`).then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: perPage };
      if (search) params.search = search;
      if (guides) params.guides = 1;
      if (!guides && category !== 'All') params.category = category;
      const { data } = await axios.get(`${API}/public/blog`, { params });
      setItems(data.items); setTotal(data.total);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [page, search, category, guides]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div data-testid="page-knowledge-hub">
      <section className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#00A79D] bg-[#00A79D]/10 px-3 py-1 rounded-full mb-5"><BookOpen className="w-3.5 h-3.5" /> {guides ? 'Care Tech Guides' : 'Knowledge Hub'}</span>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-gray-900 tracking-tight">{guides ? 'Care Tech Guides' : 'Care technology guides and insights'}</h1>
          <p className="text-lg text-gray-600 mt-4 max-w-2xl mx-auto">{guides ? 'In-depth buyer guides from the CareTechGuide.co.uk team.' : 'Independent guides, deep dives and news to help you choose and get the most from care technology.'}</p>
          <div className="mt-7 max-w-xl mx-auto relative">
            <Search className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <input value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }}
              placeholder="Search articles, guides and topics…" data-testid="kh-search"
              className="w-full pl-12 pr-4 py-3.5 border border-[#E5E7EB] rounded-full text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#00A79D]/40" />
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex gap-8">
        {/* Category sidebar (desktop) */}
        <aside className={`${guides ? 'hidden' : 'hidden lg:block'} w-60 shrink-0`} data-testid="kh-categories">
          <div className="sticky top-24">
            <p className="text-sm font-bold text-[#1A3A5F] uppercase tracking-wide mb-3">Categories</p>
            <div className="space-y-0.5 max-h-[72vh] overflow-y-auto pr-1">
              <CatButton active={category === 'All'} onClick={() => { setPage(1); setCategory('All'); }} label="All" count={meta.total} testid="kh-cat-all" />
              {meta.categories.map((c) => (
                <CatButton key={c.name} active={category === c.name} onClick={() => { setPage(1); setCategory(c.name); }}
                  label={c.name} count={c.count} testid={`kh-cat-${c.name.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`} />
              ))}
            </div>
          </div>
        </aside>

        <div className="flex-1 min-w-0">
          {/* Mobile category chips */}
          <div className={`${guides ? 'hidden' : 'lg:hidden flex'} gap-2 overflow-x-auto pb-3 mb-5 no-scrollbar`} data-testid="kh-categories-mobile">
            <button onClick={() => { setPage(1); setCategory('All'); }}
              className={`shrink-0 text-sm font-medium px-3.5 py-1.5 rounded-full border transition-colors ${category === 'All' ? 'bg-[#00A79D] text-white border-[#00A79D]' : 'bg-white text-gray-600 border-[#E5E7EB]'}`}>All ({meta.total})</button>
            {meta.categories.map((c) => (
              <button key={c.name} onClick={() => { setPage(1); setCategory(c.name); }}
                className={`shrink-0 text-sm font-medium px-3.5 py-1.5 rounded-full border transition-colors ${category === c.name ? 'bg-[#00A79D] text-white border-[#00A79D]' : 'bg-white text-gray-600 border-[#E5E7EB]'}`}>{c.name} ({c.count})</button>
            ))}
          </div>

          {loading ? (
            <div className="py-24 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : items.length === 0 ? (
            <div className="py-24 text-center text-gray-400" data-testid="kh-empty">No articles match your search.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="kh-grid">
              {items.map((a) => <ArticleCard key={a.slug} a={a} />)}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-10" data-testid="kh-pagination">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="p-2 border border-[#E5E7EB] rounded-md disabled:opacity-40" data-testid="kh-prev"><ChevronLeft className="w-4 h-4" /></button>
              <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="p-2 border border-[#E5E7EB] rounded-md disabled:opacity-40" data-testid="kh-next"><ChevronRight className="w-4 h-4" /></button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

const GuideCTA = () => (
  <div className="mt-12 rounded-2xl border border-[#00A79D]/30 bg-gradient-to-br from-[#E9F7F5] to-[#EAF6FB] p-7 sm:p-9" data-testid="guide-cta">
    <div className="flex flex-col sm:flex-row sm:items-center gap-6 justify-between">
      <div className="min-w-0">
        <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[#00857D] bg-white px-2.5 py-1 rounded-full mb-3"><ShieldCheck className="w-3.5 h-3.5" /> Independent support</div>
        <h3 className="font-heading text-2xl font-bold text-[#1A3A5F]">Need help choosing?</h3>
        <p className="text-sm sm:text-[15px] text-gray-600 mt-2 max-w-xl leading-relaxed">
          Get unbiased, one-to-one help from Liam to shortlist suppliers, pressure-test their claims and
          review contracts before you sign - no sales pitch, no paid inclusion.
        </p>
      </div>
      <div className="flex flex-col gap-3 shrink-0">
        <Link to="/book-a-call" data-testid="guide-cta-book"
          className="inline-flex items-center justify-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white text-sm font-semibold px-6 py-3 rounded-lg transition-colors">
          <PhoneCall className="w-4 h-4" /> Book a call with Liam
        </Link>
        <a href="mailto:liam@caretechguide.co.uk" data-testid="guide-cta-email"
          className="inline-flex items-center justify-center gap-2 text-[#00857D] hover:text-[#1A3A5F] text-sm font-semibold px-6 py-2 rounded-lg transition-colors">
          <Mail className="w-4 h-4" /> Or email Liam
        </a>
      </div>
    </div>
  </div>
);

const LiamBio = () => (
  <div className="mt-12 bg-gradient-to-br from-[#0f2438] to-[#1A3A5F] rounded-2xl p-7 sm:p-9 text-white" data-testid="article-author-bio">
    <div className="flex flex-col sm:flex-row gap-6 items-start">
      <div className="shrink-0 h-20 w-20 rounded-2xl bg-gradient-to-br from-[#00A79D] to-[#00AEEF] flex items-center justify-center shadow-lg" aria-hidden="true">
        <span className="font-heading font-extrabold text-2xl tracking-tight text-white">LP</span>
      </div>
      <div className="min-w-0">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#7FE3DB] bg-white/10 px-2.5 py-1 rounded-full mb-3"><ShieldCheck className="w-3.5 h-3.5" /> Independent · No paid inclusion</span>
        <h3 className="font-heading text-xl font-bold">Liam Palmer</h3>
        <p className="text-sm text-[#AEC2D6] mt-0.5">Founder &amp; Editor, CareTechGuide.co.uk</p>
        <p className="text-sm sm:text-[15px] leading-relaxed text-[#E4ECF3] mt-4">
          Liam has spent over a decade as a CQC registered manager and senior operational leader across
          private hospitals, care homes, retirement villages and mental health charities. He writes these
          guides independently - no supplier pays for inclusion or ranking - to help providers choose care
          technology with confidence.
        </p>
        <a href="mailto:liam@caretechguide.co.uk" data-testid="article-author-email"
          className="inline-flex items-center gap-2 mt-5 bg-white text-[#1A3A5F] hover:bg-[#EAF6FB] text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors">
          <Mail className="w-4 h-4" /> Email Liam
        </a>
      </div>
    </div>
  </div>
);

export const ArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [progress, setProgress] = useState(0);
  const [sections, setSections] = useState([]);
  const [navOpen, setNavOpen] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    setLoading(true); setNotFound(false); setSections([]); setProgress(0); window.scrollTo(0, 0);
    axios.get(`${API}/public/blog/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const isGuide = !!(data && data.post && data.post.is_guide);

  // Strip the leading section-number chips (00/01/FAQ/··) from guide H2s in the
  // HTML string itself, so React's dangerouslySetInnerHTML never re-injects them.
  const cleanHtml = useMemo(() => {
    const html = (data && data.post && data.post.body_html) || '';
    if (!isGuide) return html;
    return html.replace(/(<h2\b[^>]*>)\s*(?:\d{1,2}|FAQ|··|·)\s*/gi, '$1');
  }, [data, isGuide]);

  // Build the jump-to-section menu from the (already number-stripped) H2 headings.
  // Guide bodies lost their original anchor ids on import, so we scroll by index.
  useLayoutEffect(() => {
    if (!isGuide || !bodyRef.current) { setSections([]); return; }
    const hs = Array.from(bodyRef.current.querySelectorAll('h2'));
    const secs = hs.map((h, i) => ({ idx: i, label: (h.textContent || '').trim() })).filter((s) => s.label);
    setSections(secs);
  }, [isGuide, cleanHtml]);

  // Reading-progress bar tied to the article body scroll (rAF-throttled).
  useEffect(() => {
    if (!isGuide) return;
    let ticking = false;
    const compute = () => {
      ticking = false;
      const el = bodyRef.current;
      if (!el) return;
      const scrolled = window.scrollY + window.innerHeight - el.offsetTop;
      setProgress(Math.max(0, Math.min(100, (scrolled / el.offsetHeight) * 100)));
    };
    const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(compute); } };
    window.addEventListener('scroll', onScroll, { passive: true });
    compute();
    return () => window.removeEventListener('scroll', onScroll);
  }, [isGuide, cleanHtml]);

  // Unique per-article <title> + meta description (SEO) once the post has loaded.
  useEffect(() => {
    const p = data && data.post;
    if (!p) return;
    document.title = `${p.title} | Care Tech Compare`;
    const desc = (p.excerpt || p.summary || p.meta_description || p.description || p.title || '')
      .replace(/<[^>]*>/g, '').slice(0, 300);
    let m = document.head.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement('meta'); m.setAttribute('name', 'description'); document.head.appendChild(m); }
    m.setAttribute('content', desc);
    let c = document.head.querySelector('link[rel="canonical"]');
    if (!c) { c = document.createElement('link'); c.setAttribute('rel', 'canonical'); document.head.appendChild(c); }
    c.setAttribute('href', `https://caretechcompare.co.uk${window.location.pathname}`);
  }, [data]);


  const jumpTo = (idx) => {
    setNavOpen(false);
    const hs = bodyRef.current ? bodyRef.current.querySelectorAll('h2') : [];
    const el = hs[idx];
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 92;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  if (loading) return <div className="py-32 text-center text-gray-400" data-testid="article-loading"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  if (notFound || !data) return (
    <div className="max-w-2xl mx-auto px-4 py-32 text-center" data-testid="article-not-found">
      <h1 className="font-heading text-2xl font-bold text-gray-900">Article not found</h1>
      <Link to="/knowledge-hub" className="inline-flex items-center gap-1.5 text-[#00857D] font-semibold mt-4"><ArrowLeft className="w-4 h-4" /> Back to Knowledge Hub</Link>
    </div>
  );

  const p = data.post;
  const guideHero = isGuide && isRealImg(p.hero_image);
  return (
    <div data-testid="page-article">
      {/* Reading progress bar (guides) */}
      {isGuide && (
        <div className="fixed top-0 left-0 right-0 h-1 z-[60] bg-transparent" data-testid="reading-progress">
          <div className="h-full bg-gradient-to-r from-[#00A79D] to-[#00AEEF] transition-[width] duration-150 ease-out" style={{ width: `${progress}%` }} />
        </div>
      )}

      {/* Wide guide hero banner */}
      {guideHero && (
        <section className="relative overflow-hidden bg-[#0f2438]" data-testid="guide-hero">
          <img src={p.hero_image} alt={p.title} className="absolute inset-0 w-full h-full object-cover object-right opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0f2438] via-[#0f2438]/85 to-[#0f2438]/20" />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
            <button onClick={() => navigate('/knowledge-hub?guides=1')} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#AEC2D6] hover:text-white mb-6" data-testid="article-back"><ArrowLeft className="w-4 h-4" /> Care Tech Guides</button>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#0f2438] bg-[#7FE3DB] px-2.5 py-1 rounded-full"><BookOpen className="w-3.5 h-3.5" /> Buyer's Guide</span>
              {(p.categories || []).slice(0, 3).map((c) => (
                <span key={c} className="text-[11px] font-semibold uppercase tracking-wide text-white bg-white/10 px-2.5 py-1 rounded">{c}</span>
              ))}
            </div>
            <h1 className="font-heading text-3xl sm:text-5xl font-extrabold text-white leading-tight tracking-tight max-w-3xl" data-testid="article-title">{p.title}</h1>
            {p.excerpt && <p className="text-base sm:text-lg text-[#C6D5E3] mt-5 max-w-2xl leading-relaxed">{p.excerpt}</p>}
            <div className="flex flex-wrap items-center gap-4 mt-6 text-sm text-[#AEC2D6]">
              <span className="inline-flex items-center gap-1.5"><User className="w-4 h-4" /> Liam Palmer</span>
              <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {fmtDate(p.published_at)}</span>
            </div>
          </div>
        </section>
      )}

      {/* Sticky jump-to-section menu (guides) */}
      {isGuide && sections.length > 1 && (
        <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#E5E7EB]" data-testid="guide-section-nav">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <button onClick={() => setNavOpen((v) => !v)} data-testid="guide-section-nav-toggle"
              className="w-full flex items-center justify-between gap-2 py-3 text-sm font-semibold text-[#1A3A5F]">
              <span className="inline-flex items-center gap-2"><List className="w-4 h-4 text-[#00A79D]" /> On this page</span>
              <span className="text-xs font-normal text-gray-400">{sections.length} sections</span>
            </button>
            {navOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setNavOpen(false)} />
                <div className="absolute left-4 right-4 sm:left-6 sm:right-6 z-40 bg-white border border-[#E5E7EB] rounded-xl shadow-xl p-2 max-h-[60vh] overflow-y-auto" data-testid="guide-section-list">
                  {sections.map((s) => (
                    <button key={s.idx} onClick={() => jumpTo(s.idx)} data-testid={`guide-section-sec-${s.idx}`}
                      className="w-full text-left px-3 py-2 rounded-md text-sm text-gray-600 hover:bg-[#00A79D]/10 hover:text-[#00857D] transition-colors">{s.label}</button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {!guideHero && (
          <>
            <button onClick={() => navigate('/knowledge-hub')} className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-[#00857D] mb-6" data-testid="article-back"><ArrowLeft className="w-4 h-4" /> Back to Knowledge Hub</button>

            <div className="flex flex-wrap items-center gap-2 mb-4">
              {p.publisher_type && <span className="text-[11px] font-semibold uppercase tracking-wide text-white bg-[#1A3A5F] px-2.5 py-1 rounded">{p.publisher_type}</span>}
              {(p.categories || []).map((c) => (
                <Link key={c} to={`/knowledge-hub?cat=${encodeURIComponent(c)}`} className="text-[11px] font-semibold uppercase tracking-wide text-[#00857D] bg-[#00A79D]/10 px-2.5 py-1 rounded hover:bg-[#00A79D]/20">{c}</Link>
              ))}
            </div>

            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-gray-900 leading-tight tracking-tight" data-testid="article-title">{p.title}</h1>

            <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-gray-500 border-b border-[#E5E7EB] pb-6">
              <span className="inline-flex items-center gap-1.5">
                <User className="w-4 h-4" />
                {p.author_slug
                  ? <Link to={`/suppliers/${p.author_slug}`} className="text-[#00857D] font-semibold hover:underline" data-testid="article-author-link">{p.author}</Link>
                  : <span className="font-medium text-gray-700">{p.author}</span>}
              </span>
              <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {fmtDate(p.published_at)}</span>
            </div>

            {isRealImg(p.image)
              ? <img src={p.image} alt={p.title} onError={(e) => { e.target.style.display = 'none'; }}
                  className="w-full rounded-xl mt-8 border border-[#E5E7EB]" data-testid="article-image" />
              : <div className="aspect-[16/9] rounded-xl mt-8 border border-[#E5E7EB] overflow-hidden" data-testid="article-image"><BrandPlaceholder markClass="h-20 w-auto" /></div>}
          </>
        )}

        {isGuide && <GuideQuiz slug={slug} />}

        <div ref={bodyRef} className={`article-body ${guideHero ? 'mt-2' : 'mt-8'}`} data-testid="article-body" dangerouslySetInnerHTML={{ __html: cleanHtml || '<p>Full article coming soon.</p>' }} />

        {isGuide && <GuideCTA />}
        {isGuide && <LiamBio />}

        {p.author_slug && (
          <div className="mt-10 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl p-6 flex flex-wrap items-center justify-between gap-4" data-testid="article-supplier-cta">
            <div>
              <p className="text-sm text-gray-500">Written by a supplier listed on care tech compare</p>
              <p className="font-heading font-bold text-gray-900">{p.author}</p>
            </div>
            <Link to={`/suppliers/${p.author_slug}`} className="inline-flex items-center gap-1.5 bg-[#00A79D] hover:bg-[#00857D] text-white text-sm font-semibold px-5 py-2.5 rounded-md transition-colors">View listing <ArrowRight className="w-4 h-4" /></Link>
          </div>
        )}
      </article>

      {data.related && data.related.length > 0 && (
        <section className="bg-[#F9FAFB] border-t border-[#E5E7EB] mt-6">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <h2 className="font-heading text-2xl font-bold text-gray-900 mb-6">Related reading</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6" data-testid="article-related">
              {data.related.map((a) => <ArticleCard key={a.slug} a={a} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default KnowledgeHub;
